import React, { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/seo/Seo";
import { liveService } from "@/api/services";
import MatchCard from "@/components/feature/MatchCard";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Broadcast } from "@phosphor-icons/react";

const REFRESH_MS = 60_000;

export default function Live() {
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openFixture, setOpenFixture] = useState(null);
    const [details, setDetails] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const data = await liveService.scores();
                if (!cancelled) {
                    setMatches(Array.isArray(data) ? data : []);
                    setLastUpdated(new Date());
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn("Live scores load failed:", err?.message || err);
                if (!cancelled) toast.error("Couldn't refresh scores");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        const t = setInterval(load, REFRESH_MS);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, []);

    const openMatch = async (id) => {
        setOpenFixture(id);
        setDetails(null);
        try {
            setDetails(await liveService.fixture(id));
        } catch (err) {
            toast.error("Couldn't load fixture details");
        }
    };

    // Group by country for a tidier scan
    const byCountry = matches.reduce((acc, m) => {
        const key = m.country || "International";
        (acc[key] ||= []).push(m);
        return acc;
    }, {});

    return (
        <Layout>
            <Seo
                title="Live Scores"
                description="Live football scores updated every 60 seconds — top leagues from around the world. Free to view."
                path="/live"
            />

            <section className="max-w-7xl mx-auto px-4 md:px-8 pt-16">
                <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mb-3 flex items-center gap-2">
                    <Broadcast size={14} weight="fill" />
                    {matches.length > 0 ? `${matches.length} MATCHES LIVE NOW` : "LIVE SCORES"}
                </div>
                <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter">
                    Live Scores
                </h1>
                <p className="mt-4 text-zinc-400 max-w-2xl">
                    Real-time match status across global leagues. Auto-refreshes every 60
                    seconds. Click any match for venue, referee, and goal details.
                </p>
                {lastUpdated && (
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-3">
                        UPDATED · {lastUpdated.toLocaleTimeString()}
                    </p>
                )}
            </section>

            <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 space-y-12">
                {loading ? (
                    <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 pulse-sharp">
                        LOADING LIVE FIXTURES...
                    </p>
                ) : matches.length === 0 ? (
                    <div className="border border-dashed border-border p-12 text-center">
                        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                            NO LIVE MATCHES RIGHT NOW
                        </p>
                        <p className="text-zinc-400 text-sm mt-3 max-w-md mx-auto">
                            The scoreboard's empty between fixtures. Most action kicks off
                            evenings UK time on Saturdays, Sundays, Tuesdays and Wednesdays.
                        </p>
                    </div>
                ) : (
                    Object.entries(byCountry).map(([country, list]) => (
                        <div key={country} data-testid={`live-country-${country}`}>
                            <h2 className="text-xl md:text-2xl font-bold tracking-tight mb-4 border-b border-border pb-3 flex items-baseline justify-between">
                                <span>{country}</span>
                                <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                                    {list.length} {list.length === 1 ? "match" : "matches"}
                                </span>
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {list.map((m) => (
                                    <MatchCard
                                        key={m.id}
                                        match={m}
                                        onClick={() => openMatch(m.id)}
                                        testIdPrefix="live-card"
                                    />
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </section>

            <Dialog open={!!openFixture} onOpenChange={(o) => !o && setOpenFixture(null)}>
                <DialogContent className="bg-card border-border rounded-none max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-bold tracking-tight">
                            Match Details
                        </DialogTitle>
                    </DialogHeader>
                    {!details ? (
                        <p className="font-mono text-xs uppercase tracking-widest pulse-sharp text-zinc-500">
                            LOADING...
                        </p>
                    ) : (
                        <div className="space-y-3 text-sm" data-testid="live-modal-content">
                            <p className="font-bold text-lg">
                                {details.home} vs {details.away}
                            </p>
                            <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                                {details.league} · {details.country}
                            </p>
                            <Row label="Score">
                                {details.goalsHome ?? 0} : {details.goalsAway ?? 0}
                            </Row>
                            <Row label="Status">
                                {details.status?.long || details.status?.short || "—"}
                            </Row>
                            <Row label="Venue">{details.venue || "—"}</Row>
                            <Row label="Referee">{details.referee || "—"}</Row>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex justify-between border-t border-border pt-2">
            <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                {label}
            </span>
            <span className="text-white">{children}</span>
        </div>
    );
}
